var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import retry from 'async-retry';
import moment from 'moment';
import { BASE_10, CRYPTO_CONNECT_INVALID, EMAIL_INVALID, EMAIL_OR_USER_DOES_NOT_EXIST, HTTP_ERROR_BAD_REQUEST, HTTP_ERROR_FORBIDDEN, HTTP_ERROR_NOT_AUTHORIZED, HTTP_ERROR_NOT_FOUND, HTTP_ERROR_SERVER_ERROR, HTTP_ERROR_SERVICE_UNAVAILABLE, INCORRECT_PASSWORD_PROVIDED, LOGIN_INVALID, PARROT_TOKEN_CLAIM_INVALID, PASSWORD_INVALID, PURCHASE_CONNECTED_WALLET_REQUIRED, PURCHASE_EVENT_CLOSED, PURCHASE_EVENT_DOES_NOT_EXIST, PURCHASE_EVENT_FULL, PURCHASE_INSUFFICIENT_FUNDS, PURCHASE_INVALID_OUTCOME, PURCHASE_LOCATION_NOT_PROVIDED, PURCHASE_NFT_REQUIRED, REGISTER_EMAIL_EXISTS, RESET_PASSWORD_INVALID_KEY, RESET_PASSWORD_INVALID_PASSWORD, STATE_PROVINCE_INVALID, UNKNOWN_ERROR, USERNAME_CHANGE_TIMEOUT, USERNAME_INVALID, USERNAME_NOT_AVAILABLE, } from './constants';
import { LOCATION_REQUIRE_ASK, LOCATION_REQUIRE_NEVER, } from './interfaces/IGeolocation';
import { AUTHORIZATION_ERROR_NOT_AUTHORIZED, CONFIGURATION_ERROR_NOT_FETCHED } from './interfaces/IRawAuthorizationError';
import { TEAM_VENUE_DOME, TEAM_VENUE_OUTDOORS, TEAM_VENUE_RETRACTABLE, } from './interfaces/ITeam';
import { appendError } from './utils/errors';
import GeolocationData, { LOCATION_PERMISSION_DENIED, LOCATION_REQUEST_FUNCTION_MISSING, LOCATION_SERVICES_DISABLED, } from './utils/geolocation';
import translateRawProductGroup from './utils/translateRawProductGroup';
const HTTP_STATUS_OK_START = 200;
const HTTP_STATUS_OK_END = 299;
const HTTP_STATUS_BAD_REQUEST = 400;
const HTTP_STATUS_NOT_AUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const HTTP_STATUS_NOT_FOUND = 404;
const HTTP_STATUS_SERVER_ERROR_START = 500;
const HTTP_STATUS_SERVER_ERROR_END = 501;
const HTTP_STATUS_SERVICE_UNAVAILABLE_START = 502;
const HTTP_STATUS_SERVICE_UNAVAILABLE_END = 504;
const defaultApiOptions = {
    authenticationMethod: 'session',
    autoAuth: true,
    baseUrl: 'http://local.ogs.host/',
    customerRefreshInterval: 30000,
    fetchRetryOptions: {
        factor: 1.66093,
        minTimeout: 50,
        retries: 10,
    },
    geolocationRequired: LOCATION_REQUIRE_NEVER,
};
export default class API {
    constructor(options) {
        this.autoAuthTimeoutId = undefined;
        this.baseFetchOptions = {
            headers: {},
        };
        this.currentCustomer = undefined;
        this.onAutoAuthCallbacks = [];
        this.onLoginCallbacks = [];
        this.onLogoutCallbacks = [];
        this.options = Object.assign({}, defaultApiOptions);
        this.fetchCurrentCustomer = async () => {
            var _a;
            if (!this.configuration) {
                return Promise.reject({ type: CONFIGURATION_ERROR_NOT_FETCHED });
            }
            const userId = (_a = this.authorization) === null || _a === void 0 ? void 0 : _a.id;
            if (!userId) {
                return Promise.reject({ type: AUTHORIZATION_ERROR_NOT_AUTHORIZED });
            }
            const customerUrl = await this.getQueryUrlWithGeolocation(`/v1/customers/${userId}/`);
            const rawCustomer = await this.get(customerUrl);
            return Promise.resolve(this.translateCustomer(rawCustomer));
        };
        this.updateGeolocationData = (geolocation) => {
            this.geolocation = new GeolocationData(geolocation);
            return this.geolocation;
        };
        this.getGeolocationData = async () => {
            if (this.geolocation) {
                return Promise.resolve(this.geolocation);
            }
            if (!this.options.clientRequestGeolocationData) {
                const error = { type: LOCATION_REQUEST_FUNCTION_MISSING };
                return Promise.reject(error);
            }
            const geolocation = await this.options.clientRequestGeolocationData();
            const geolocationData = this.updateGeolocationData(geolocation);
            return Promise.resolve(geolocationData);
        };
        this.getQueryUrlWithGeolocation = async (url) => {
            if (this.options.geolocationRequired === LOCATION_REQUIRE_NEVER) {
                return Promise.resolve(url);
            }
            try {
                const geolocation = await this.getGeolocationData();
                return Promise.resolve(this.getQueryUrl(url, geolocation));
            }
            catch (error) {
                const { type } = error;
                const catchableErrors = [LOCATION_PERMISSION_DENIED, LOCATION_SERVICES_DISABLED].find((errorType) => (errorType === type));
                if (!catchableErrors) {
                    return Promise.reject(error);
                }
                const allowRequireGeolocationState = [LOCATION_REQUIRE_ASK, LOCATION_REQUIRE_NEVER].find((requireGeolocation) => (requireGeolocation === this.options.geolocationRequired));
                if (allowRequireGeolocationState) {
                    return Promise.resolve(url);
                }
                return Promise.reject(error);
            }
        };
        this.translateAuthorization = (rawAuthorization) => ({
            apiKey: rawAuthorization.api_key,
            csrfToken: rawAuthorization['X-CSRFToken'],
            id: rawAuthorization.id,
        });
        let convertedOptions;
        if (typeof options === 'string') {
            convertedOptions = {
                baseUrl: options,
            };
        }
        else {
            convertedOptions = Object.assign({}, options);
        }
        this.options = Object.assign(Object.assign({}, this.options), convertedOptions);
        this.fetchConfiguration();
        if (this.options.autoAuth) {
            this.startAutoAuth();
        }
    }
    async attemptAuthorization() {
        try {
            const authListResponse = await this.get('/v1/auth/');
            const rawAuthorization = authListResponse.objects[0];
            if (!rawAuthorization) {
                const unauthorizedError = { type: AUTHORIZATION_ERROR_NOT_AUTHORIZED };
                return Promise.reject(unauthorizedError);
            }
            this.authorization = this.translateAuthorization(rawAuthorization);
            return Promise.resolve();
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    async attemptCryptoAuthorization() {
        try {
            const authListResponse = await this.get('/v1/crypto_auth/');
            const rawAuthorization = authListResponse.objects[0];
            if (!rawAuthorization) {
                const unauthorizedError = { type: AUTHORIZATION_ERROR_NOT_AUTHORIZED };
                return Promise.reject(unauthorizedError);
            }
            this.authorization = this.translateAuthorization(rawAuthorization);
            return Promise.resolve();
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    async changeAvatar(image) {
        try {
            const data = new FormData();
            const avatarData = typeof image === 'string' ? new File([image], image) : image;
            data.append('avatar', avatarData);
            await this.fetch('/v1/avatars/', {
                body: data,
                method: 'POST',
            });
        }
        catch (error) {
            return Promise.reject([error]);
        }
    }
    async changePassword(providedData) {
        try {
            const data = {
                new_password: providedData.newPassword,
                password: providedData.password,
                username: providedData.username,
            };
            await this.post('/v2/editpassword/', data);
            await this.attemptAuthorization();
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (data === null || data === void 0 ? void 0 : data.editpassword) {
                    const { editpassword } = data;
                    if (editpassword.password === 'The password you specified was not correct.') {
                        appendError(errors, 'email', {
                            message: providedData.password,
                            type: INCORRECT_PASSWORD_PROVIDED,
                        });
                    }
                }
                return Promise.reject(errors);
            }
            return Promise.reject([error]);
        }
    }
    async resetPassword(requestData) {
        var _a;
        try {
            const rawRequestData = {
                key: requestData.token,
                password1: requestData.password1,
                password2: requestData.password2,
                usrnm: requestData.userId,
            };
            await this.post('/v1/forgotpasswordkey/', rawRequestData);
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_NOT_AUTHORIZED) {
                const errors = { type: HTTP_ERROR_BAD_REQUEST };
                if ((_a = data === null || data === void 0 ? void 0 : data.forgotpasswordkey) === null || _a === void 0 ? void 0 : _a.key) {
                    appendError(errors, 'key', {
                        message: data.forgotpasswordkey.key,
                        type: RESET_PASSWORD_INVALID_KEY
                    });
                }
                if (data.forgotpasswordkey.password) {
                    data.forgotpasswordkey.password.forEach((message) => appendError(errors, 'key', {
                        message,
                        type: RESET_PASSWORD_INVALID_PASSWORD
                    }));
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async createChatAuthToken() {
        try {
            const rawChatAuth = await this.get('/v1/chat/auth/');
            return {
                token: rawChatAuth.token,
                userId: rawChatAuth.user_id,
            };
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    async fetchConfiguration() {
        try {
            const rawConfig = await this.get('/v1/configuration/');
            this.configuration = rawConfig;
            return Promise.resolve();
        }
        catch (error) {
            return Promise.reject(error);
        }
    }
    fetchPlayerNote(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/playernote/', options);
            this.get(url)
                .then((rawPlayerNotes) => {
                const playerNotes = rawPlayerNotes.teams.map((rawPlayerNote) => {
                    const rawPlayerNoteDetails = JSON.parse(rawPlayerNote.noteJson);
                    const playerNoteDetails = {
                        statsglobalid: rawPlayerNoteDetails.statsglobalid,
                        update: rawPlayerNoteDetails.update,
                        injuryStatus: rawPlayerNoteDetails.injury_status,
                        headline: rawPlayerNoteDetails.headline,
                        position: rawPlayerNoteDetails.position,
                        teamstatsglobalid: rawPlayerNoteDetails.teamstatsglobalid,
                        analysis: rawPlayerNoteDetails.analysis,
                        injuryDescription: rawPlayerNoteDetails.injury_description,
                        firstname: rawPlayerNoteDetails.firstname,
                        injured: rawPlayerNoteDetails.injured,
                        lastname: rawPlayerNoteDetails.lastname
                    };
                    const playerNote = {
                        noteDate: rawPlayerNote.noteDate,
                        noteDateSeq: rawPlayerNote.noteDateSeq,
                        noteType: rawPlayerNote.noteType,
                        playerId: rawPlayerNote.playerId,
                        teamId: rawPlayerNote.teamId,
                        noteDetails: playerNoteDetails
                    };
                    return playerNote;
                });
                yes({ playerNotes });
            }, no);
        });
    }
    async forgotPassword(providedData) {
        var _a;
        try {
            const data = {
                email: providedData.email,
            };
            await this.post('/v1/forgotpassword/', data);
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (((_a = data.forgotpassword) === null || _a === void 0 ? void 0 : _a.email) === 'Email or username does not exist.') {
                    appendError(errors, 'email', {
                        message: providedData.email,
                        type: EMAIL_OR_USER_DOES_NOT_EXIST,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject([error]);
        }
    }
    async updateProfile(providedData) {
        var _a, _b, _c;
        try {
            const data = {};
            if (providedData.address1) {
                data.address1 = providedData.address1;
            }
            if (providedData.address2) {
                data.address2 = providedData.address2;
            }
            if (providedData.city) {
                data.city = providedData.city;
            }
            if (providedData.dob) {
                data.dob = providedData.dob;
            }
            if (providedData.phone) {
                data.phone = providedData.phone;
            }
            if (providedData.postal) {
                data.postal = providedData.postal;
            }
            if (providedData.firstName || providedData.lastName || providedData.username) {
                data.user = {};
                if (providedData.firstName) {
                    data.user.first_name = providedData.firstName;
                }
                if (providedData.lastName) {
                    data.user.last_name = providedData.lastName;
                }
                if (providedData.username) {
                    data.user.username = providedData.username;
                }
            }
            const customer = this.translateCustomer(await this.put(`/v1/customers/${(_a = this.authorization) === null || _a === void 0 ? void 0 : _a.id}/`, data));
            if (providedData.username && this.options.authenticationMethod === 'token' && this.authorization) {
                this.authorization.username = customer.user.username;
            }
            return customer;
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if ((_b = data.customers) === null || _b === void 0 ? void 0 : _b.username) {
                    const usernameTimeoutRegex = /Username may be updated only once every (\d+) days/g;
                    const usernameTimeoutRegexResult = usernameTimeoutRegex.exec(data.customers.username);
                    if (usernameTimeoutRegexResult !== null) {
                        appendError(errors, 'username', {
                            data: { timeoutDays: parseInt(usernameTimeoutRegexResult[1], BASE_10) },
                            message: data.customers.username,
                            type: USERNAME_CHANGE_TIMEOUT,
                        });
                    }
                    else if (data.customers.username === 'Username is not available') {
                        appendError(errors, 'username', {
                            message: data.customers.username,
                            type: USERNAME_NOT_AVAILABLE,
                        });
                    }
                    else if (data.customers.username) {
                        appendError(errors, 'username', {
                            message: (_c = data.customers) === null || _c === void 0 ? void 0 : _c.username,
                            type: USERNAME_INVALID,
                        });
                    }
                }
                return Promise.reject(errors);
            }
            return Promise.reject([error]);
        }
    }
    delete(url) {
        return this.fetch(url, {
            method: 'DELETE',
        });
    }
    fetchCurrentAndNextKenoDraws() {
        return new Promise((yes, no) => {
            this.fetchNextKenoDraw()
                .then((nextDraw) => {
                if (nextDraw) {
                    const currentId = String(parseInt(nextDraw.id, BASE_10) - 1);
                    if (currentId) {
                        this.fetchKenoDraws({ id: currentId })
                            .then((currentDraws) => {
                            const currentDraw = currentDraws[0];
                            yes([currentDraw, nextDraw]);
                        }, no);
                    }
                }
                else {
                    yes([]);
                }
            }, no);
        });
    }
    translateCustomer(rawCustomer) {
        var _a, _b;
        let customerAvatar = '';
        if (rawCustomer.avatar.url !== 'no-avatar.png') {
            customerAvatar = ((_b = (_a = this.configuration) === null || _a === void 0 ? void 0 : _a.customerAvatarRepo) !== null && _b !== void 0 ? _b : '') + rawCustomer.avatar;
        }
        const customer = {
            accounts: {
                fantasyCash: rawCustomer.account.accounts.F,
                holdAccount: rawCustomer.account.accounts.H,
                doubleDownBonus: rawCustomer.account.accounts.L,
                mainAccount: rawCustomer.account.accounts.M,
                pointsAccount: rawCustomer.account.accounts.P,
                wageringRequirement: rawCustomer.account.accounts.R
            },
            address1: rawCustomer.address1,
            address2: rawCustomer.address2,
            avatar: {
                createdTimestamp: rawCustomer.avatar.created_ts,
                url: customerAvatar,
            },
            balance: rawCustomer.account.balance,
            city: rawCustomer.city,
            country: rawCustomer.country,
            dob: rawCustomer.dob,
            id: rawCustomer.id,
            phone: rawCustomer.phone,
            postal: rawCustomer.postal,
            province: {
                abbreviation: rawCustomer.province.abbreviation,
                country: rawCustomer.province.country,
                freeContestsOnly: rawCustomer.province.free_pools_only,
                id: rawCustomer.province.id,
                majorityAge: rawCustomer.province.majority_age,
            },
            user: {
                email: rawCustomer.user.email,
                firstName: rawCustomer.user.first_name,
                lastName: rawCustomer.user.last_name,
                username: rawCustomer.user.username,
            },
        };
        if (rawCustomer.eth_address) {
            customer.ethAddress = rawCustomer.eth_address;
        }
        if (rawCustomer.current_province) {
            customer.currentProvince = {
                abbreviation: rawCustomer.current_province.abbreviation,
                country: rawCustomer.current_province.country,
                freeContestsOnly: rawCustomer.current_province.free_pools_only,
                id: rawCustomer.current_province.id,
                majorityAge: rawCustomer.current_province.majority_age,
            };
        }
        return customer;
    }
    async fetchBets(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/bets/', options);
            this.get(url)
                .then((rawBets) => {
                const bets = rawBets.objects.map((rawBet) => {
                    let scoringRules;
                    let bracketBet;
                    if (typeof rawBet.settings_json.outcome_scoring_range !== 'undefined') {
                        const ranges = rawBet
                            .settings_json.outcome_scoring_range
                            .map((range) => {
                            return {
                                end: range[1],
                                start: range[0],
                                value: range[2],
                            };
                        });
                        scoringRules = {
                            ranges,
                            type: 'range',
                        };
                    }
                    else if (typeof rawBet.settings_json.outcome_scoring !== 'undefined') {
                        const rawScoringRules = rawBet.settings_json.outcome_scoring;
                        scoringRules = Object.assign(Object.assign({}, rawScoringRules), { type: 'win/lose' });
                    }
                    if (rawBet.bracket_bet) {
                        bracketBet = {
                            betId: rawBet.bracket_bet.bet_id,
                            gameNumber: rawBet.bracket_bet.game_number,
                            id: rawBet.bracket_bet.id,
                            quadrant: rawBet.bracket_bet.quadrant,
                            roundNumber: rawBet.bracket_bet.round_number,
                        };
                    }
                    return {
                        betChoices: rawBet.bet_choices.map((rawChoices) => {
                            return {
                                betId: rawChoices.bet_id,
                                id: rawChoices.id,
                                outcomeId: rawChoices.outcome_id,
                                outcomeName: rawChoices.outcome_name,
                                outcomeType: rawChoices.outcome_type,
                                sortOrder: rawChoices.sort_order,
                            };
                        }),
                        bracketBet,
                        description: rawBet.description,
                        eventGameId: rawBet.event_game_id,
                        finalValue: rawBet.final_value !== null ? rawBet.final_value : undefined,
                        id: rawBet.id,
                        isPush: rawBet.is_push,
                        scoringField: rawBet.scoring_field,
                        scoringRules,
                        scoringType: rawBet.scoring_type,
                        sortOrder: rawBet.sort_order,
                        summary: rawBet.summary,
                        widgetChoice: rawBet.widget_choice,
                    };
                });
                yes(bets);
            }, no);
        });
    }
    translateFantasyGames(rawGames) {
        const gameIds = Object.keys(rawGames).map((key) => (parseInt(key, BASE_10)));
        return gameIds.map((gameId) => {
            const rawGame = rawGames[gameId];
            return {
                gameStatus: rawGame.s,
                gameTime: rawGame.gt,
                gameUnit: rawGame.gu,
                homeTeamId: rawGame.hti,
                homeTeamName: rawGame.htn,
                homeTeamScore: rawGame.hts,
                id: rawGame.i,
                visitingTeamId: rawGame.vti,
                visitingTeamName: rawGame.vtn,
                visitingTeamScore: rawGame.vts,
                week: rawGame.wk
            };
        });
    }
    translateFantasyBoxScores(rawGames) {
        const gameIds = Object.keys(rawGames).map((key) => (parseInt(key, BASE_10)));
        return gameIds.reduce((reducerBoxScores, gameId) => {
            const rawGame = rawGames[gameId];
            const rawPlayerBoxScoreIds = Object
                .keys(rawGame.p)
                .map((key) => parseInt(key, BASE_10));
            const league = rawGame.l;
            const boxscores = rawPlayerBoxScoreIds.map((playerBoxScoreId) => {
                const _a = rawGame.p[playerBoxScoreId], { ei: externalId, i: id, t: teamId } = _a, rawBoxscores = __rest(_a, ["ei", "i", "t"]);
                const translatedBoxscores = {
                    externalId,
                    id,
                    teamId,
                };
                if (league === 'MLB') {
                    return Object.assign(Object.assign({}, translatedBoxscores), { batter_caught_stealing: rawBoxscores.bcs, batter_doubles: rawBoxscores.bd, batter_hit_by_pitch: rawBoxscores.bhbp, batter_home_runs: rawBoxscores.bhr, batter_rbi: rawBoxscores.brbi, batter_runs_scored: rawBoxscores.brs, batter_singles: rawBoxscores.bs, batter_stolen_bases: rawBoxscores.bsb, batter_triples: rawBoxscores.bt, batter_walks: rawBoxscores.bw, pitcher_complete_game: rawBoxscores.pcg, pitcher_complete_game_shutout: rawBoxscores.pcgs, pitcher_earned_runs_allowed: rawBoxscores.pera, pitcher_hit_batter: rawBoxscores.phb, pitcher_hits_against: rawBoxscores.pha, pitcher_innings_pitched: rawBoxscores.pip, pitcher_no_hitter: rawBoxscores.pnh, pitcher_strike_outs: rawBoxscores.pso, pitcher_walks_against: rawBoxscores.pwa, pitcher_wins: rawBoxscores.pw });
                }
                else if (league === 'NBA') {
                    return Object.assign(Object.assign({}, translatedBoxscores), { assists: rawBoxscores.a, blocks: rawBoxscores.b, double_double: rawBoxscores.dd, field_goals_missed: rawBoxscores.fgs_miss, points: rawBoxscores.p, rebounds: rawBoxscores.r, steals: rawBoxscores.s, technical_fouls: rawBoxscores.tf, three_pts_made: rawBoxscores.tpm, triple_double: rawBoxscores.td, turnovers: rawBoxscores.to });
                }
                else if (league === 'NFL') {
                    return Object.assign(Object.assign({}, translatedBoxscores), { def_2pt_return: rawBoxscores.dpr, def_fumbles_recovered: rawBoxscores.dfr, def_ints: rawBoxscores.di, def_kicks_blocked: rawBoxscores.dkb, def_pts_allowed: rawBoxscores.dpa, def_sacks: rawBoxscores.ds, def_safety: rawBoxscores.dsy, def_tds: rawBoxscores.dt, fumbles_lost: rawBoxscores.fl, kick_return_tds: rawBoxscores.krt, kicker_fg_0_19_made: rawBoxscores.kfgma, kicker_fg_0_19_missed: rawBoxscores.kfgmas, kicker_fg_20_29_made: rawBoxscores.kfgmb, kicker_fg_20_29_missed: rawBoxscores.kfgmbs, kicker_fg_30_39_made: rawBoxscores.kfgmc, kicker_fg_30_39_missed: rawBoxscores.kfgmcs, kicker_fg_40_49_made: rawBoxscores.kfgmd, kicker_fg_40_49_missed: rawBoxscores.kfgmds, kicker_fg_50_plus_made: rawBoxscores.kfgme, kicker_fg_50_plus_missed: rawBoxscores.kfgmes, kicker_fg_yds: rawBoxscores.kfgy, kicker_fgs_made: rawBoxscores.kfgm, kicker_fgs_missed: rawBoxscores.kfgms, kicker_xp_made: rawBoxscores.kxm, kicker_xp_missed: rawBoxscores.kxms, off_ints: rawBoxscores.oi, own_fumbles_recovered_tds: rawBoxscores.ofrt, pass_300_yds_bonus: rawBoxscores.pyb, pass_tds: rawBoxscores.pt, pass_two_pt: rawBoxscores.ptp, pass_yds: rawBoxscores.py, punt_return_tds: rawBoxscores.prt, receiving_100_yds_bonus: rawBoxscores.rxyb, receiving_tds: rawBoxscores.rxt, receiving_yds: rawBoxscores.rxy, receptions: rawBoxscores.r, rush_100_yds_bonus: rawBoxscores.ryb, rush_tds: rawBoxscores.rt, rush_yds: rawBoxscores.ry, team_wins: rawBoxscores.team_wins, two_pt_conversion: rawBoxscores.tpc });
                }
                else if (league === 'NHL') {
                    return Object.assign(Object.assign({}, translatedBoxscores), { assists: rawBoxscores.a, blocked_shots: rawBoxscores.bs, goalie_goals_against: rawBoxscores.gga, goalie_saves: rawBoxscores.gs, goalie_wins: rawBoxscores.gw, goals: rawBoxscores.g, hat_trick: rawBoxscores.ht, short_handed_assists: rawBoxscores.sha, short_handed_goals: rawBoxscores.shg, shots_on_goal: rawBoxscores.sog });
                }
                else if (league === 'PGA') {
                    return Object.assign(Object.assign({}, translatedBoxscores), { birdie: rawBoxscores.b, birdie_streak_3: rawBoxscores.bs3, birdie_streak_4: rawBoxscores.bs4, birdie_streak_5: rawBoxscores.bs5, bogey: rawBoxscores.bo, bogey_free_round: rawBoxscores.bfr, double_bogey: rawBoxscores.db, double_eagle: rawBoxscores.de, eagle: rawBoxscores.e, hole_in_one: rawBoxscores.hio, par: rawBoxscores.p, tournamentPosition: rawBoxscores.sjtp, triple_bogey_plus: rawBoxscores.tb });
                }
                return translatedBoxscores;
            });
            return reducerBoxScores.concat(boxscores);
        }, []);
    }
    translateFantasyOutcomes(rawOutcomes) {
        const outcomeIds = Object.keys(rawOutcomes).map((key) => (parseInt(key, BASE_10)));
        return outcomeIds.map((outcomeId) => {
            const rawOutcome = rawOutcomes[outcomeId];
            return {
                closeTimestamp: rawOutcome.c,
                externalId: rawOutcome.ei,
                id: rawOutcome.i,
                imageUrl: rawOutcome.iu,
                name: rawOutcome.n,
                plannedPoints: rawOutcome.pp,
                pointsAvailable: rawOutcome.pa,
                selectionCost: rawOutcome.sc,
                typeName: rawOutcome.t,
            };
        });
    }
    translateFantasyEvent(rawEvent, outcomeIds) {
        let rawSettings;
        try {
            rawSettings = JSON.parse(rawEvent.settings_json);
        }
        catch (exception) {
            rawSettings = {};
        }
        const event = {
            eventGamesConfig: rawEvent.evgcn,
            blacklistedEntrantsUsernames: rawEvent.bleu,
            closeTs: rawEvent.c,
            containsBlacklistedEntrants: rawEvent.ble,
            context: rawEvent.ctx,
            description: rawEvent.d,
            draftTs: rawEvent.dt,
            externalId: rawEvent.eid,
            finalizeTs: rawEvent.f,
            gameType: rawEvent.gt,
            geolocationRequired: rawEvent.gr,
            id: rawEvent.i,
            isGuaranteed: this.determineIsGuaranteed(rawEvent.tmn),
            isUserBlacklisted: rawEvent.bl,
            outcomeIds,
            prizes: this.translateEventPrizes(rawEvent.pse),
            status: rawEvent.s,
            ticketCost: rawEvent.tc,
            ticketCostCurrency: rawEvent.tcc,
            ticketCount: rawEvent.tcnt,
            ticketMax: rawEvent.tm,
            ticketMin: rawEvent.tmn
        };
        if (rawSettings.additional_links) {
            event.additionalLinks = rawSettings.additional_links;
        }
        if (rawSettings.default_outcome_scoring) {
            event.defaultOutcomeScoring = Object.assign(Object.assign({}, rawSettings.default_outcome_scoring), { type: 'win/lose' });
        }
        if (rawSettings.info_graphic_url) {
            event.infoGraphicUrl = rawSettings.info_graphic_url;
        }
        if (rawSettings.prize_tooltip_graphic_url) {
            event.prizeTooltipGraphicUrl = rawSettings.prize_tooltip_graphic_url;
        }
        if (rawSettings.chat_group_name) {
            event.chatGroupName = rawSettings.chat_group_name;
        }
        return event;
    }
    translateFantasyTickets(eventId, rawTickets) {
        const ticketIds = Object.keys(rawTickets).map((key) => (parseInt(key, BASE_10)));
        return ticketIds.map((ticketId) => {
            const rawTicket = rawTickets[ticketId];
            let rawPrize = {};
            if (rawTicket.pd) {
                rawPrize = JSON.parse(rawTicket.pd);
            }
            else if (rawTicket.awe) {
                rawPrize = rawTicket.awe;
            }
            const prizes = [];
            if (rawPrize.external) {
                prizes.push({
                    amount: rawPrize.external.a,
                    description: rawPrize.external.d,
                    type: 'external',
                });
            }
            if (rawPrize.fc) {
                prizes.push({
                    amount: rawPrize.fc.a,
                    type: 'fc',
                });
            }
            if (rawPrize.qualifier) {
                prizes.push({
                    amount: rawPrize.qualifier.a,
                    description: rawPrize.qualifier.d,
                    type: 'qualifier',
                });
            }
            if (rawPrize.usd) {
                prizes.push({
                    amount: rawPrize.usd.a,
                    description: rawPrize.usd.d,
                    type: 'usd',
                });
            }
            if (rawPrize.pts) {
                prizes.push({
                    amount: rawPrize.pts.a,
                    description: rawPrize.pts.d,
                    type: 'pts',
                });
            }
            return {
                amountWon: rawTicket.aw,
                eventId,
                id: rawTicket.id,
                pointsEarned: rawTicket.p,
                position: rawTicket.r,
                positionTied: rawTicket.t,
                prizes,
                selections: rawTicket.s.map((selection) => (selection.i)),
                status: rawTicket.st,
                user: {
                    avatar: rawTicket.a,
                    username: rawTicket.u,
                },
            };
        });
    }
    translateFantasySelections(rawTickets) {
        const ticketIds = Object.keys(rawTickets).map((key) => (parseInt(key, BASE_10)));
        return ticketIds.reduce((selections, ticketId) => {
            const rawTicket = rawTickets[ticketId];
            const ticketSelections = rawTicket.s.reduce((reducerTicketSelections, rawSelection) => {
                if (typeof rawSelection.b === 'number') {
                    reducerTicketSelections.push({
                        betId: rawSelection.b,
                        id: rawSelection.i,
                        losingOutcomeId: rawSelection.lo,
                        outcomeId: rawSelection.o,
                        pointsEarned: rawSelection.p,
                        ticketId,
                        value: rawSelection.v,
                    });
                }
                else if (typeof rawSelection.evp === 'number') {
                    reducerTicketSelections.push({
                        eventPositionId: rawSelection.evp,
                        id: rawSelection.i,
                        losingOutcomeId: rawSelection.lo,
                        outcomeId: rawSelection.o,
                        pointsEarned: rawSelection.p,
                        ticketId,
                    });
                }
                return reducerTicketSelections;
            }, []);
            return selections.concat(ticketSelections);
        }, []);
    }
    async fetchFantasyEvents(eventId, requestData) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl(`/v1/fantasy/events/${eventId}`, requestData);
            this.get(url).then((rawFantasyEvent) => {
                const games = this.translateFantasyGames(rawFantasyEvent.g);
                const boxscores = this.translateFantasyBoxScores(rawFantasyEvent.g);
                const outcomes = this.translateFantasyOutcomes(rawFantasyEvent.o);
                const outcomeIds = outcomes.map((outcome) => outcome.id);
                const event = this.translateFantasyEvent(rawFantasyEvent, outcomeIds);
                if (rawFantasyEvent.t !== undefined) {
                    const tickets = this.translateFantasyTickets(event.id, rawFantasyEvent.t);
                    const selections = this.translateFantasySelections(rawFantasyEvent.t);
                    yes({ boxscores, event, games, outcomes, selections, tickets });
                }
                else {
                    yes({ boxscores, event, games, outcomes });
                }
            }, no);
        });
    }
    async fetchFantasyEventGames(eventGamesId, requestData) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl(`/v1/fantasy/eventgames/${eventGamesId}`, requestData);
            this.get(url)
                .then((rawFantasyEventGames) => {
                const games = this.translateFantasyGames(rawFantasyEventGames.g);
                const gameIds = games.map((game) => game.id);
                const outcomes = this.translateFantasyOutcomes(rawFantasyEventGames.o);
                const outcomeIds = outcomes.map((outcome) => outcome.id);
                const settings = JSON.parse(rawFantasyEventGames.evgsj);
                let bracketConfig = undefined;
                if (rawFantasyEventGames.brcfg !== undefined && rawFantasyEventGames.brcfg !== null) {
                    bracketConfig = {
                        quadrants: JSON.parse(rawFantasyEventGames.brcfg.qdtsj),
                        reseed: rawFantasyEventGames.brcfg.rs,
                        rounds: JSON.parse(rawFantasyEventGames.brcfg.rdsj),
                    };
                }
                const eventGames = {
                    bracketConfig,
                    closeEventTs: rawFantasyEventGames.c,
                    config: {
                        gameType: rawFantasyEventGames.cfggt,
                        gameVersion: settings.game_version,
                        name: rawFantasyEventGames.cfg,
                    },
                    games: gameIds,
                    id: rawFantasyEventGames.i,
                    league: rawFantasyEventGames.cxt,
                    settings,
                };
                if (rawFantasyEventGames.evt !== undefined && rawFantasyEventGames.evt !== null) {
                    const event = this.translateFantasyEvent(rawFantasyEventGames.evt, outcomeIds);
                    yes({ event, eventGames, games, outcomes });
                }
                else {
                    yes({ eventGames, games, outcomes });
                }
            }, no);
        });
    }
    async addFriends(friends) {
        return new Promise((yes, no) => {
            const rawFriends = {
                friends: friends.usernames,
            };
            this.post('/v1/friends/', rawFriends).then((rawResult) => {
                const result = rawResult;
                yes(result);
            }, no);
        });
    }
    removeFriend(userId) {
        return new Promise((yes, no) => {
            this.delete(`/v1/friends/${userId}/`).then(yes, no);
        });
    }
    fetchFriends() {
        return new Promise((yes, no) => {
            this.get('/v1/friends/').then((response) => {
                const rawFriends = response.objects[0];
                const rawFriendsMembers = typeof rawFriends !== 'undefined' ? rawFriends.m : [];
                const friends = (rawFriendsMembers).map((rawFriend) => {
                    var _a;
                    return ({
                        avatar: `${(_a = this.configuration) === null || _a === void 0 ? void 0 : _a.customerAvatarRepo}${rawFriend.avatar}`,
                        id: rawFriend.id,
                        username: rawFriend.username,
                    });
                });
                yes(friends);
            }, no);
        });
    }
    determineIsGuaranteed(ticketMin) {
        return ticketMin === 1;
    }
    async fetchEvent(id) {
        var _a;
        let eventUrl = `/v1/events/${id}/`;
        if (['always', 'ask'].includes((_a = this.options.geolocationRequired) !== null && _a !== void 0 ? _a : 'never')) {
            eventUrl = await this.getQueryUrlWithGeolocation(`/v1/events/${id}/`);
            eventUrl = eventUrl + '&check_location=1';
        }
        return new Promise((yes, no) => {
            this.get(eventUrl)
                .then((rawEvent) => {
                const event = {
                    context: rawEvent.context,
                    description: rawEvent.description,
                    draftTs: rawEvent.draft_ts,
                    eventGamesConfig: rawEvent.eventgamesconfig,
                    externalId: rawEvent.external_id,
                    gameType: rawEvent.game_type,
                    geolocationRequired: rawEvent.geolocation_required,
                    id: rawEvent.id,
                    isGuaranteed: this.determineIsGuaranteed(rawEvent.ticket_min),
                    locationPermitted: rawEvent.location_permitted,
                    payout: rawEvent.payout,
                    prizes: this.translateEventPrizes(rawEvent.payout_structure || []),
                    requiredNFTs: typeof rawEvent.required_nfts !== 'undefined' ? this.translateRequiredNFTs(rawEvent.required_nfts) : undefined,
                    settingsJson: rawEvent.settings_json,
                    status: rawEvent.status,
                    ticketCost: rawEvent.ticket_cost,
                    ticketCostCurrency: rawEvent.ticket_cost_currency,
                    ticketCount: rawEvent.ticket_count,
                    ticketMax: rawEvent.ticket_max,
                    ticketMin: rawEvent.ticket_min
                };
                if (rawEvent.close_ts) {
                    event.closeTs = rawEvent.close_ts;
                }
                if (rawEvent.finalize_ts) {
                    event.finalizeTs = rawEvent.finalize_ts;
                }
                if (rawEvent.open_ts) {
                    event.openTs = rawEvent.open_ts;
                }
                const settings = JSON.parse(rawEvent.settings_json);
                if (settings.prize_tooltip_graphic_url) {
                    event.prizeTooltipGraphicUrl = settings.prize_tooltip_graphic_url;
                }
                if (settings.additional_links) {
                    event.additionalLinks = settings.additional_links;
                }
                if (settings.info_graphic_url) {
                    event.infoGraphicUrl = settings.info_graphic_url;
                }
                if (settings.sort_order) {
                    event.sortOrder = settings.sort_order;
                }
                else {
                    event.sortOrder = 0;
                }
                if (settings.default_outcome_scoring) {
                    event.defaultOutcomeScoring = Object.assign(Object.assign({}, settings.default_outcome_scoring), { type: 'win/lose' });
                }
                if (settings.chat_group_name) {
                    event.chatGroupName = settings.chat_group_name;
                }
                yes(event);
            }, no);
        });
    }
    translateRequiredNFTs(rawRequiredNFTs) {
        return rawRequiredNFTs.map((rawRequiredNFT) => {
            return {
                address: rawRequiredNFT.address,
                chain: rawRequiredNFT.chain,
                id: rawRequiredNFT.id,
                logoImageUrl: rawRequiredNFT.logo_image_url,
                mintLink: rawRequiredNFT.mint_link,
                name: rawRequiredNFT.name,
                settingsJson: rawRequiredNFT.settings_json,
            };
        });
    }
    fetchFriendsRequests() {
        return new Promise((yes, no) => {
            this.get('/v1/friends/requests/')
                .then((response) => {
                const rawFriends = response.objects;
                const friendRequests = rawFriends.map((rawFriend) => {
                    var _a;
                    return ({
                        avatar: `${(_a = this.configuration) === null || _a === void 0 ? void 0 : _a.customerAvatarRepo}${rawFriend.avatar}`,
                        id: rawFriend.id,
                        requestedByUserId: rawFriend.requested_by_user_id,
                        username: rawFriend.username,
                    });
                });
                yes(friendRequests);
            }, no);
        });
    }
    acceptFriendsRequests(friends) {
        return new Promise((yes, no) => {
            const rawFriendsRequests = {
                friends: friends.usernames,
            };
            this.post('/v1/friends/requests/', rawFriendsRequests)
                .then((rawResult) => {
                const result = rawResult;
                yes(result);
            }, no);
        });
    }
    rejectFriendsRequests(userId) {
        return new Promise((yes, no) => {
            this.delete(`/v1/friends/requests/${userId}/`).then(yes, no);
        });
    }
    fetchEventGame(id) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl(`/v1/eventgames/${id}/`);
            this.get(url)
                .then((rawEventGames) => {
                const eventgames = [this.translateRawEventGames(rawEventGames)];
                const games = this.translateRawEventGamesGames(rawEventGames);
                const teams = this.translateRawEventGamesTeams(rawEventGames);
                yes({ eventgames, games, teams });
            }, no);
        });
    }
    fetchEventGames(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/eventgames/', options);
            this.get(url)
                .then((rawEventGamesList) => {
                const eventgames = rawEventGamesList.objects.map(this.translateRawEventGames);
                const games = rawEventGamesList.objects.reduce((reducedGames, rawEventGames) => {
                    const parsedGames = this.translateRawEventGamesGames(rawEventGames);
                    return [...reducedGames, ...parsedGames];
                }, []);
                const teams = rawEventGamesList.objects.reduce((reducedTeams, rawEventGames) => {
                    const parsedGameTeams = this.translateRawEventGamesTeams(rawEventGames);
                    return [...reducedTeams, ...parsedGameTeams];
                }, []);
                yes({ eventgames, games, teams });
            }, no);
        });
    }
    translateEventPrizes(rawPrizes) {
        return rawPrizes.map((rawPrize) => {
            return {
                end: rawPrize.end,
                items: rawPrize.payouts.map((rawPrizeItem) => {
                    if (rawPrizeItem.type === 'cash') {
                        return {
                            amount: rawPrizeItem.amount,
                            currency: rawPrizeItem.currency,
                            type: 'cash',
                        };
                    }
                    if (rawPrizeItem.type === 'qualifier') {
                        return {
                            description: rawPrizeItem.description,
                            type: 'qualifier',
                        };
                    }
                    return {
                        amount: rawPrizeItem.amount,
                        currency: rawPrizeItem.currency,
                        description: rawPrizeItem.description,
                        type: 'external',
                    };
                }),
                start: rawPrize.start,
            };
        });
    }
    translateEvent(rawEvent) {
        const { close_ts, finalize_ts, open_ts } = rawEvent;
        const event = {
            closeTs: rawEvent.close_ts,
            context: rawEvent.context,
            description: rawEvent.description,
            draftTs: rawEvent.draft_ts,
            eventGamesConfig: '',
            externalId: rawEvent.external_id,
            finalizeTs: rawEvent.finalize_ts,
            gameType: rawEvent.game_type,
            geolocationRequired: rawEvent.geolocation_required,
            id: rawEvent.id,
            isGuaranteed: this.determineIsGuaranteed(rawEvent.ticket_min),
            openTs: rawEvent.open_ts,
            payout: 0,
            prizeTooltipGraphicUrl: rawEvent.prize_tooltip_graphic_url,
            prizes: this.translateEventPrizes(rawEvent.payout_structure || []),
            requiredNFTs: typeof rawEvent.required_nfts !== 'undefined' ? this.translateRequiredNFTs(rawEvent.required_nfts) : undefined,
            settingsJson: rawEvent.settings_json,
            sortOrder: 0,
            status: rawEvent.status,
            ticketCost: rawEvent.ticket_cost,
            ticketCostCurrency: rawEvent.ticket_cost_currency,
            ticketCount: rawEvent.ticket_count,
            ticketMax: rawEvent.ticket_max,
            ticketMin: rawEvent.ticket_min
        };
        if (close_ts) {
            event.closeTs = close_ts;
        }
        if (finalize_ts) {
            event.finalizeTs = finalize_ts;
        }
        if (open_ts) {
            event.openTs = open_ts;
        }
        const settings = JSON.parse(rawEvent.settings_json);
        if (settings.additional_links) {
            event.additionalLinks = settings.additional_links;
        }
        if (settings.info_graphic_url) {
            event.infoGraphicUrl = settings.info_graphic_url;
        }
        if (settings.sort_order) {
            event.sortOrder = settings.sort_order;
        }
        if (settings.default_outcome_scoring) {
            event.defaultOutcomeScoring = Object.assign(Object.assign({}, settings.default_outcome_scoring), { type: 'win/lose' });
        }
        if (settings.chat_group_name) {
            event.chatGroupName = settings.chat_group_name;
        }
        if (settings.eventgamesconfig) {
            event.eventGamesConfig = settings.eventgamesconfig;
        }
        return event;
    }
    fetchEvents(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/events/', options);
            this.get(url)
                .then((rawEvents) => {
                const events = rawEvents.objects.map((rawEvent) => (this.translateEvent(rawEvent)));
                yes(events);
            }, no);
        });
    }
    fetchGameWeather(options) {
        return new Promise((yes, no) => {
            const url = '/v1/gameweather/';
            const urlWithQueryStringOptions = this.getQueryUrl(url, options);
            this.get(urlWithQueryStringOptions)
                .then((response) => {
                const rawGameWeatherList = response.objects;
                const gameWeatherList = rawGameWeatherList.map((rawGameWeatherItem) => {
                    var _a;
                    const weatherJsonData = JSON.parse(rawGameWeatherItem.weather_json);
                    const type = !weatherJsonData.displayHourly ? 'current' :
                        ((_a = weatherJsonData.venues[0]) === null || _a === void 0 ? void 0 : _a.hourly) ? 'hourly' : 'daily';
                    return Object.assign(Object.assign({}, weatherJsonData), { comment: rawGameWeatherItem.weather_comment, gameId: rawGameWeatherItem.game.id, id: rawGameWeatherItem.id, scale: rawGameWeatherItem.weather_scale, type });
                });
                yes(gameWeatherList);
            }, no);
        });
    }
    fetchGames(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/game/', options);
            this.get(url)
                .then((rawGames) => {
                const games = rawGames.objects.map((rawGame) => {
                    if (rawGame.league === 'PGA') {
                        const parsedSettings = JSON.parse(rawGame.settings_json);
                        const game = {
                            chatGroupName: parsedSettings.chat_group_name,
                            course: parsedSettings.clubs,
                            externalId: rawGame.external_id,
                            finalized: rawGame.finalized,
                            gameCodeGlobalId: rawGame.game_code_global_id,
                            gameStatus: rawGame.game_status,
                            gameTime: rawGame.game_time,
                            id: rawGame.id,
                            league: rawGame.league,
                            provider: rawGame.provider,
                            season: rawGame.season,
                            timeRemaining: rawGame.time_remaining,
                            tournamentName: parsedSettings.tournament_name,
                            tournamentDisplayName: parsedSettings.tournament_display_name,
                        };
                        return game;
                    }
                    else if (rawGame.league === 'MMA') {
                        const parsedSettings = JSON.parse(rawGame.settings_json);
                        const game = {
                            chatGroupName: parsedSettings.chat_group_name,
                            externalId: rawGame.external_id,
                            finalized: rawGame.finalized,
                            gameCodeGlobalId: rawGame.game_code_global_id,
                            gameStatus: rawGame.game_status,
                            gameTime: rawGame.game_time,
                            id: rawGame.id,
                            league: rawGame.league,
                            provider: rawGame.provider,
                            season: rawGame.season,
                            timeRemaining: rawGame.time_remaining,
                            tournamentDisplayName: parsedSettings.name,
                            tournamentName: parsedSettings.short_name,
                        };
                        return game;
                    }
                    else if (rawGame.league === 'NASCAR') {
                        const parsedSettings = JSON.parse(rawGame.settings_json);
                        const game = {
                            chatGroupName: parsedSettings.chat_group_name,
                            course: parsedSettings.track,
                            externalId: rawGame.external_id,
                            finalized: rawGame.finalized,
                            gameCodeGlobalId: rawGame.game_code_global_id,
                            gameStatus: rawGame.game_status,
                            gameTime: rawGame.game_time,
                            id: rawGame.id,
                            league: rawGame.league,
                            provider: rawGame.provider,
                            season: rawGame.season,
                            timeRemaining: rawGame.time_remaining,
                            tournamentDisplayName: parsedSettings.name,
                            tournamentName: parsedSettings.name,
                        };
                        return game;
                    }
                    else {
                        const parsedSettings = JSON.parse(rawGame.settings_json);
                        const game = {
                            chatGroupName: parsedSettings.chat_group_name,
                            externalId: rawGame.external_id,
                            finalized: rawGame.finalized,
                            gameCodeGlobalId: rawGame.game_code_global_id,
                            gameStatus: rawGame.game_status,
                            gameTime: rawGame.game_time,
                            gameUnit: rawGame.game_unit,
                            homeTeamId: rawGame.home_team.id,
                            homeTeamName: rawGame.home_team.name,
                            homeTeamScore: rawGame.home_team_score,
                            id: rawGame.id,
                            odds: rawGame.odds,
                            league: rawGame.league,
                            provider: rawGame.provider,
                            season: rawGame.season,
                            timeRemaining: rawGame.time_remaining,
                            visitingTeamId: rawGame.visiting_team.id,
                            visitingTeamName: rawGame.visiting_team.name,
                            visitingTeamScore: rawGame.visiting_team_score,
                            week: rawGame.week,
                        };
                        return game;
                    }
                });
                const teams = rawGames.objects.reduce((reducedTeams, rawGame) => {
                    const parsedGameTeams = this.translateRawGamesTeams(rawGame);
                    return [...reducedTeams, ...parsedGameTeams];
                }, []);
                yes({ games, teams });
            }, no);
        });
    }
    async fetchSupportSubjects() {
        return new Promise((yes, no) => {
            this.get('/v1/contact/').then((response) => {
                yes(response.subjectChoices);
            }, no);
        });
    }
    async submitSupportRequest(data) {
        const rawData = { body: data.body, email: data.email, subject: data.subject };
        if (data.firstName) {
            rawData.fname = data.firstName;
        }
        if (data.lastName) {
            rawData.lname = data.lastName;
        }
        return new Promise((yes, no) => {
            this.post('/v1/contact/', rawData).then(() => {
                yes();
            }, no);
        });
    }
    translateRawGamesTeams(rawGame) {
        const inferTeamVenueType = (domeType) => {
            switch (domeType) {
                case TEAM_VENUE_DOME:
                    return 'dome';
                case TEAM_VENUE_OUTDOORS:
                    return 'outdoor';
                case TEAM_VENUE_RETRACTABLE:
                    return 'retractable-roof';
                default:
                    return 'indoor';
            }
        };
        const teams = [rawGame.home_team, rawGame.visiting_team]
            .reduce((reducerTeams, rawTeam) => {
            if (rawTeam) {
                const parsedTeamSettings = rawTeam.settings_json.length > 0
                    ? JSON.parse(rawTeam.settings_json)
                    : undefined;
                const teamVenue = parsedTeamSettings ? {
                    bearing: parsedTeamSettings.bearing,
                    city: parsedTeamSettings.stadium_city,
                    coordinates: {
                        lat: parsedTeamSettings.latitude,
                        long: parsedTeamSettings.longitude,
                    },
                    country: parsedTeamSettings.stadium_country,
                    name: parsedTeamSettings.stadium_name,
                    state: parsedTeamSettings.stadium_state,
                    type: inferTeamVenueType(parsedTeamSettings.dome),
                } : undefined;
                const team = {
                    alias: rawTeam.alias,
                    city: rawTeam.city,
                    conference: rawTeam.conference,
                    division: rawTeam.division,
                    externalId: rawTeam.external_id,
                    id: rawTeam.id,
                    league: rawTeam.league,
                    losses: rawTeam.losses,
                    name: rawTeam.name,
                    provider: rawTeam.provider,
                    season: rawTeam.season,
                    teamCodeGlobalId: rawTeam.team_code_global_id,
                    venue: teamVenue,
                    wins: rawTeam.wins
                };
                return [...reducerTeams, team];
            }
            return reducerTeams;
        }, []);
        return teams.reduce((uniqueReducerTeams, team) => {
            if (uniqueReducerTeams.find((uniqueTeam) => uniqueTeam.id === team.id)) {
                return uniqueReducerTeams;
            }
            return [...uniqueReducerTeams, team];
        }, []);
    }
    async editPropBetsTicketSelections(ticketId, deletedSelectionIds, newSelections) {
        var _a;
        try {
            const rawEditSelections = {
                deleted_objects: deletedSelectionIds,
                objects: newSelections.map((object) => {
                    if (object.value) {
                        if (object.outcomeId) {
                            return {
                                bet_id: object.betId,
                                outcome_id: object.outcomeId,
                                losing_outcome_id: object.losingOutcomeId ? object.losingOutcomeId : undefined,
                                value: object.value,
                            };
                        }
                        else {
                            return {
                                bet_id: object.betId,
                                value: object.value,
                            };
                        }
                    }
                    else {
                        return {
                            bet_id: object.betId,
                            outcome_id: object.outcomeId,
                            losing_outcome_id: object.losingOutcomeId ? object.losingOutcomeId : undefined,
                        };
                    }
                }),
                ticket_id: ticketId,
            };
            const response = await this.patch('/v1/selections/', rawEditSelections);
            return response.objects;
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if ((_a = data === null || data === void 0 ? void 0 : data.dev_message) === null || _a === void 0 ? void 0 : _a.event) {
                    if (data.dev_message.event.includes('This contest is closed.')) {
                        appendError(errors, 'event', {
                            message: 'This event is closed.',
                            type: PURCHASE_EVENT_CLOSED
                        });
                    }
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    fetchKenoDraws(options, type = 'default') {
        return new Promise((yes, no) => {
            const url = type === 'default' ? '/v1/draws/keno/' : '/v1/draws/keno-igt/';
            const fullUrl = this.getQueryUrl(url, options);
            this.get(fullUrl)
                .then((response) => {
                const draws = response.objects.map((raw) => {
                    const drawTimestamp = raw.actual_draw_ts ? moment.utc(raw.actual_draw_ts).local().unix() : undefined;
                    const scheduledDrawTimestamp = raw.scheduled_draw_ts ? moment.utc(raw.scheduled_draw_ts).local().unix() : undefined;
                    return {
                        createdTimestamp: moment.utc(raw.created_ts).unix(),
                        drawTimestamp,
                        id: String(raw.id),
                        modifiedTimestamp: moment.utc(raw.modified_ts).unix(),
                        results: raw.results_json,
                        scheduledDrawTimestamp,
                        settings: raw.settings_json,
                        status: raw.status,
                    };
                });
                yes(draws);
            }, no);
        });
    }
    fetchKenoIGTDraws(options) {
        return this.fetchKenoDraws(options, 'igt');
    }
    fetchNextKenoDraw() {
        return new Promise((yes, no) => {
            this.fetchKenoDraws({
                sort_by: 'id',
                status: 'open',
            }).then((openDraws) => {
                openDraws.sort((a, b) => {
                    const aValue = parseInt(a.id, BASE_10);
                    const bValue = parseInt(b.id, BASE_10);
                    return aValue - bValue;
                });
                yes(openDraws[0]);
            }, no);
        });
    }
    fetchMarkets() {
        return new Promise((yes, no) => {
            this.get('/v1/markets/').then((response) => {
                const rawMarkets = response.objects;
                const markets = (rawMarkets).map((rawMarket) => {
                    const rawMarketSettings = JSON.parse(rawMarket.settings_json || '{}');
                    const marketSettings = {};
                    if (typeof rawMarketSettings.chat_group_name !== 'undefined') {
                        marketSettings.chat = {
                            id: rawMarketSettings.chat_group_name,
                            type: 'group',
                        };
                    }
                    return {
                        active: rawMarket.active,
                        createdTimestamp: rawMarket.created_ts,
                        icon: rawMarket.icon,
                        id: rawMarket.id,
                        modifiedTimestamp: rawMarket.modified_ts,
                        name: rawMarket.name,
                        order: rawMarket.order,
                        settings: marketSettings,
                    };
                });
                yes(markets);
            }, no);
        });
    }
    fetchMarketEvents() {
        return new Promise((yes, no) => {
            this.get('/v1/marketevents/').then((response) => {
                const rawMarketEvents = response.objects;
                const marketEvents = (rawMarketEvents).map((rawMarketEvent) => {
                    const rawMarketEventSettings = JSON.parse(rawMarketEvent.settings_json || '{}');
                    const marketEventSettings = {};
                    if (typeof rawMarketEventSettings.chat_group_name !== 'undefined') {
                        marketEventSettings.chat = {
                            id: rawMarketEventSettings.chat_group_name,
                            type: 'group',
                        };
                    }
                    return {
                        active: rawMarketEvent.active,
                        createdTimestamp: rawMarketEvent.created_ts,
                        eventIds: rawMarketEvent.events.map((rawEvent) => (rawEvent.id)),
                        eventTimestamp: rawMarketEvent.event_ts,
                        icon: rawMarketEvent.icon,
                        id: rawMarketEvent.id,
                        marketId: rawMarketEvent.market_id,
                        modifiedTimestamp: rawMarketEvent.modified_ts,
                        name: rawMarketEvent.name,
                        settings: marketEventSettings,
                    };
                });
                const events = rawMarketEvents.reduce((reducerEvents, rawMarketEvent) => {
                    const translatedEvents = rawMarketEvent.events.map((event) => this.translateEvent(event));
                    return [...reducerEvents, ...translatedEvents];
                }, []);
                yes({ events, marketEvents });
            }, no);
        });
    }
    fetchMarketCategories() {
        return new Promise((yes, no) => {
            this.get('/v1/marketcategories/').then((response) => {
                const rawMarketCategories = response.objects;
                const marketcategories = (rawMarketCategories).map((rawMarketCategory) => {
                    return {
                        createdTimestamp: rawMarketCategory.created_ts,
                        id: rawMarketCategory.id,
                        marketEventId: rawMarketCategory.market_event_id,
                        modifiedTimestamp: rawMarketCategory.modified_ts,
                        name: rawMarketCategory.name
                    };
                });
                yes(marketcategories);
            }, no);
        });
    }
    fetchMarketOutcomes(options) {
        return new Promise((yes, no) => {
            const fetchQuery = this.camelToSnakeObject(options);
            const url = this.getQueryUrl('/v1/marketoutcomes/', fetchQuery);
            this.get(url).then((response) => {
                const rawMarketOutcomes = response.objects;
                const marketoutcomes = (rawMarketOutcomes).map((rawMarketOutcome) => {
                    return {
                        createdTimestamp: rawMarketOutcome.created_ts,
                        icon: rawMarketOutcome.icon ? rawMarketOutcome.icon : undefined,
                        id: rawMarketOutcome.id,
                        marketCategoryId: rawMarketOutcome.market_category_id,
                        modifiedTimestamp: rawMarketOutcome.modified_ts,
                        name: rawMarketOutcome.name,
                        odds: rawMarketOutcome.odds,
                        supportingData: rawMarketOutcome.supporting_data,
                        winner: rawMarketOutcome.winner
                    };
                });
                yes(marketoutcomes);
            }, no);
        });
    }
    fetchOutcomes(options) {
        return new Promise((yes, no) => {
            const fetchQuery = this.camelToSnakeObject(options);
            const url = this.getQueryUrl('/v1/outcomes/', fetchQuery);
            this.get(url)
                .then((response) => {
                const rawOutcomes = response.objects;
                const outcomes = rawOutcomes.map((raw) => {
                    let bracketOutcome;
                    if (raw.bracket_outcome) {
                        bracketOutcome = {
                            byes: raw.bracket_outcome.byes,
                            id: raw.bracket_outcome.id,
                            quadrant: raw.bracket_outcome.quadrant,
                            seed: raw.bracket_outcome.seed,
                        };
                    }
                    return {
                        bracketOutcome,
                        closeTimestamp: raw.close_ts || undefined,
                        externalId: String(raw.external_id),
                        id: raw.id,
                        imageUrl: raw.image_url,
                        name: raw.name,
                        plannedPoints: raw.planned_points,
                        pointsAvailable: raw.points_available,
                        relatedCollection: raw.related_collection ? String(raw.related_collection) : undefined,
                        selectionCost: raw.selection_cost,
                        typeName: raw.type_name,
                    };
                });
                yes(outcomes);
            }, no);
        });
    }
    fetchProductGroups(options = {}) {
        return this.fetchList('/v1/productgroups/', options, translateRawProductGroup);
    }
    fetchProductPlayTypeByName(name) {
        return new Promise((yes, no) => {
            this.fetch(`/v1/productplaytypes/?name=${name}`)
                .then((raw) => {
                const ppt = raw.objects[0];
                if (ppt) {
                    const productPlayType = this.translateRawProductPlayType(ppt);
                    yes(productPlayType);
                }
                else {
                    yes(ppt);
                }
            }, no);
        });
    }
    fetchProductPlayTypeComponents(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/productplaytypecomponents/', options);
            this.fetch(url)
                .then((response) => {
                const { objects } = response;
                const components = objects.map((raw) => {
                    const { id, max, min, outcometype, productplaytype, settings_json } = raw;
                    return {
                        id: String(id),
                        max,
                        min,
                        outcomeType: {
                            description: outcometype.description,
                            id: String(outcometype.id),
                            name: outcometype.name,
                        },
                        productPlayTypeUri: productplaytype,
                        settings: settings_json,
                    };
                });
                yes(components);
            }, no);
        });
    }
    fetchProductPlayTypes(options = {}) {
        return this.fetchList('/v1/productplaytypes/', options, this.translateRawProductPlayType);
    }
    fetchProducts(options = {}) {
        return this.fetchList('/v1/products/', options, this.translateRawProduct);
    }
    fetchProvinces() {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/stateprovince');
            this.get(url)
                .then((rawProvinces) => {
                const provinces = rawProvinces.objects.map((rawProvince) => {
                    return {
                        abbreviation: rawProvince.abbreviation,
                        country: rawProvince.country,
                        name: rawProvince.name,
                        registrationBonusCode: rawProvince.registration_bonus_code,
                    };
                });
                yes(provinces);
            }, no);
        });
    }
    translateRawSelection(rawSelection, ticketId) {
        const selection = {
            id: rawSelection.id,
            outcomeId: rawSelection.outcome_id,
            losingOutcomeId: rawSelection.losing_outcome_id,
            pointsEarned: rawSelection.points_earned,
            ticketId,
        };
        const betId = rawSelection.bet_id;
        if (typeof betId !== 'undefined') {
            return Object.assign(Object.assign({}, selection), { betId, value: rawSelection.value });
        }
        const eventPositionId = rawSelection.event_position_id;
        return Object.assign(Object.assign({}, selection), { eventPositionId });
    }
    fetchSelections(ticketId) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/selections/', { ticket: ticketId });
            this.get(url)
                .then((rawSelections) => {
                const selections = rawSelections.objects.map((rawSelection) => {
                    return this.translateRawSelection(rawSelection, ticketId);
                });
                yes(selections);
            }, no);
        });
    }
    fetchTickets(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/tickets/', options, (key) => {
                if (key.includes('event')) {
                    return key.replace('event', 'event_');
                }
                return key;
            });
            this.get(url)
                .then((rawTickets) => {
                const tickets = rawTickets.objects.map((rawTicket) => {
                    return {
                        amountWon: rawTicket.amount_won,
                        cost: rawTicket.cost,
                        createdTimestamp: moment.utc(rawTicket.created_ts).unix(),
                        eventId: rawTicket.event.id,
                        id: rawTicket.id,
                        pointsEarned: rawTicket.points_earned,
                        position: rawTicket.position,
                        positionTied: rawTicket.position_tied,
                        selections: (rawTicket.selections || [])
                            .reduce((selectionIds, selection) => {
                            if (typeof selection.id !== 'undefined') {
                                selectionIds.push(selection.id);
                            }
                            return selectionIds;
                        }, []),
                        status: rawTicket.status,
                        wager: rawTicket.wager,
                        wagerCurrency: rawTicket.wager_currency,
                    };
                });
                const selections = rawTickets.objects
                    .reduce((reducerSelections, rawTicket) => {
                    const rawTicketSelections = rawTicket.selections || [];
                    const ticketSelections = rawTicketSelections.map((rawSelection) => {
                        return this.translateRawSelection(rawSelection, rawTicket.id);
                    });
                    return reducerSelections.concat(ticketSelections);
                }, []);
                const events = rawTickets.objects.map((rawTicket) => {
                    return {
                        additionalLinks: rawTicket.event.additional_links,
                        closeTs: rawTicket.event.close_ts,
                        context: rawTicket.event.context,
                        description: rawTicket.event.description,
                        eventGamesConfig: rawTicket.event.eventgamesconfig,
                        externalId: String(rawTicket.event.external_id),
                        gameType: rawTicket.event.game_type,
                        geolocationRequired: rawTicket.event.geolocation_required,
                        id: rawTicket.event.id,
                        isGuaranteed: this.determineIsGuaranteed(rawTicket.event.ticket_min),
                        infoGraphicUrl: rawTicket.event.info_graphic_url,
                        prizeTooltipGraphicUrl: rawTicket.event.prize_tooltip_graphic_url,
                        prizes: this.translateEventPrizes(rawTicket.event.payout_structure || []),
                        requiredNFTs: typeof rawTicket.event.required_nfts !== 'undefined' ? this.translateRequiredNFTs(rawTicket.event.required_nfts) : undefined,
                        sortOrder: 0,
                        status: rawTicket.event.status,
                        ticketCost: rawTicket.event.ticket_cost,
                        ticketCostCurrency: rawTicket.event.ticket_cost_currency,
                        ticketCount: rawTicket.event.ticket_count,
                        ticketMax: rawTicket.event.ticket_max,
                        ticketMin: rawTicket.event.ticket_min
                    };
                });
                yes({ events, selections, tickets });
            }, no);
        });
    }
    fetchTransactions(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/transactions/', options);
            this.get(url)
                .then((rawTransactions) => {
                const transactions = rawTransactions.objects.map((rawTx) => {
                    const tx = {
                        amount: rawTx.amount,
                        createdTimestamp: moment.utc(rawTx.created_ts).unix(),
                        currency: rawTx.currency,
                        description: rawTx.description,
                        id: rawTx.id,
                        name: rawTx.name,
                    };
                    return tx;
                });
                yes(transactions);
            }, no);
        });
    }
    async signupNewsletter(email) {
        try {
            await this.post('/v1/newsletter/', { email });
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST && (data === null || data === void 0 ? void 0 : data.email)) {
                return Promise.reject({
                    data: {
                        email: [{
                                message: data.email[0],
                                type: EMAIL_INVALID
                            }]
                    },
                    type: HTTP_ERROR_BAD_REQUEST
                });
            }
            return Promise.reject(error);
        }
    }
    async get(url) {
        const { fetchRetryOptions } = this.options;
        return retry((bail) => {
            const fetchPromise = this.fetch(url);
            fetchPromise.catch((error) => {
                if (error instanceof Error) {
                    const { message } = error;
                    if (message === 'Network request failed') {
                        return;
                    }
                    bail(error);
                    return;
                }
                if (error.type === HTTP_ERROR_SERVICE_UNAVAILABLE) {
                    return;
                }
                bail(error);
            });
            return fetchPromise;
        }, fetchRetryOptions);
    }
    getCurrentCustomer() {
        return this.currentCustomer;
    }
    async fetchUsers(username) {
        const url = this.getQueryUrl('/v1/customers/', { username });
        try {
            const response = await this.get(url);
            const rawUsers = response.objects;
            const users = rawUsers.map((rawUser) => {
                var _a;
                return {
                    avatar: `${(_a = this.configuration) === null || _a === void 0 ? void 0 : _a.customerAvatarRepo}${rawUser.avatar}`,
                    id: rawUser.id,
                    username: rawUser.user.username,
                };
            });
            return Promise.resolve(users);
        }
        catch (error) {
            return Promise.reject({
                data: error,
                type: UNKNOWN_ERROR,
            });
        }
    }
    async login(credentials) {
        try {
            const authUrl = await this.getQueryUrlWithGeolocation('/v1/auth/');
            const data = {
                captcha: credentials.captcha,
                password: credentials.password,
                rsu_gps_available: credentials.gpsAvailable,
                rsu_platform: credentials.platform,
                username: credentials.username,
            };
            const rawAuthorization = await this.post(authUrl, data);
            this.authorization = Object.assign(Object.assign({}, this.translateAuthorization(rawAuthorization)), { username: this.options.authenticationMethod === 'token' ? credentials.username : undefined });
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_NOT_AUTHORIZED) {
                const errors = { type };
                if (data.incorrect) {
                    appendError(errors, 'general', {
                        message: data.incorrect,
                        type: LOGIN_INVALID,
                    });
                }
                else if (data.password) {
                    appendError(errors, 'password', {
                        message: data.incorrect,
                        type: LOGIN_INVALID,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async fetchCustomerCryptoNonce(address) {
        const url = this.getQueryUrl('/v1/crypto_nonce/', { address });
        try {
            const response = await this.get(url);
            return Promise.resolve(response.nonce);
        }
        catch (error) {
            return Promise.reject({
                data: error,
                type: UNKNOWN_ERROR,
            });
        }
    }
    async fetchParrotPassTokenClaimStatus(pass) {
        try {
            const response = await this.get(`/v1/claim_parrot_tokens/${pass}/`);
            const claimStatus = this.translateRawParrotPassTokenClaimStatus(response);
            return Promise.resolve(claimStatus);
        }
        catch (error) {
            return Promise.reject({
                data: error,
                type: UNKNOWN_ERROR,
            });
        }
    }
    async fetchParrotPassesTokenClaimStatus() {
        try {
            const response = await this.get('/v1/claim_parrot_tokens/');
            const claimStatus = response.passes.map(this.translateRawParrotPassTokenClaimStatus);
            return Promise.resolve(claimStatus);
        }
        catch (error) {
            return Promise.reject({
                data: error,
                type: UNKNOWN_ERROR,
            });
        }
    }
    async claimParrotTokens(passes) {
        try {
            const data = {
                passes_to_claim: passes
            };
            const response = await this.post('/v1/claim_parrot_tokens/', data);
            const claimStatus = response.passes.map(this.translateRawParrotPassTokenClaimStatus);
            return Promise.resolve(claimStatus);
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (data.claim_parrot_tokens.passes) {
                    appendError(errors, 'passes', {
                        message: data.claim_parrot_tokens.passes,
                        type: PARROT_TOKEN_CLAIM_INVALID,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async cryptoConnect(credentials) {
        var _a;
        try {
            const data = {
                address: credentials.address,
                signature: credentials.signature,
                disconnect: credentials.disconnect
            };
            await this.put(`/v1/crypto_connect/${(_a = this.authorization) === null || _a === void 0 ? void 0 : _a.id}/`, data);
            const customer = this.fetchCurrentCustomer();
            return Promise.resolve(customer);
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (data.crypto_connect.signature) {
                    appendError(errors, 'signature', {
                        message: data.crypto_connect.signature,
                        type: CRYPTO_CONNECT_INVALID,
                    });
                }
                else if (data.crypto_connect.address) {
                    appendError(errors, 'address', {
                        message: data.crypto_connect.address,
                        type: CRYPTO_CONNECT_INVALID,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async cryptoLogin(credentials) {
        try {
            const authUrl = await this.getQueryUrlWithGeolocation('/v1/crypto_auth/');
            const data = {
                captcha: credentials.captcha,
                address: credentials.address,
                rsu_gps_available: credentials.gpsAvailable,
                rsu_platform: credentials.platform,
                signature: credentials.signature,
            };
            const rawAuthorization = await this.post(authUrl, data);
            this.authorization = Object.assign(Object.assign({}, this.translateAuthorization(rawAuthorization)), { address: this.options.authenticationMethod === 'token' ? credentials.address : undefined });
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_NOT_AUTHORIZED) {
                const errors = { type };
                if (data.incorrect) {
                    appendError(errors, 'general', {
                        message: data.incorrect,
                        type: LOGIN_INVALID,
                    });
                }
                else if (data.address) {
                    appendError(errors, 'address', {
                        message: data.incorrect,
                        type: LOGIN_INVALID,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async logout() {
        await this.delete('/v1/auth/');
        await this.delete('/v1/crypto_auth/');
    }
    onLogin(callback) {
        this.onLoginCallbacks.push(callback);
    }
    onLogout(callback) {
        this.onLogoutCallbacks.push(callback);
    }
    post(url, data) {
        return this.fetch(url, {
            body: JSON.stringify(data),
            method: 'POST',
        });
    }
    patch(url, data) {
        return this.fetch(url, {
            body: JSON.stringify(data),
            method: 'PATCH',
        });
    }
    async purchasePropBetsTicket(selections, eventId) {
        var _a, _b, _c, _d, _e, _f;
        const purchaseTicketUrl = await this.getQueryUrlWithGeolocation('/v1/tickets/');
        const postData = {
            event: `/api/v1/events/${eventId}/`,
            selections: selections.map((selection) => {
                const { betId, losingOutcomeId, outcomeId, value } = selection;
                return {
                    bet_id: betId,
                    outcome_id: outcomeId,
                    losing_outcome_id: losingOutcomeId,
                    value: value ? value : undefined,
                };
            }),
        };
        try {
            const rawTicket = await this.post(purchaseTicketUrl, postData);
            return {
                amountWon: 0,
                cost: rawTicket.cost,
                createdTimestamp: moment.utc(rawTicket.created_ts).unix(),
                eventId: rawTicket.event.id,
                id: rawTicket.id,
                selections: (rawTicket.selections || []).map((rawSelection) => rawSelection.id),
                status: rawTicket.status,
            };
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_FORBIDDEN) {
                const errors = { type };
                if ((_a = data === null || data === void 0 ? void 0 : data.message) === null || _a === void 0 ? void 0 : _a.location) {
                    appendError(errors, 'location', {
                        message: data.message.location,
                        type: PURCHASE_LOCATION_NOT_PROVIDED
                    });
                }
                if ((_b = data === null || data === void 0 ? void 0 : data.message) === null || _b === void 0 ? void 0 : _b.event) {
                    if (data.message.event.includes('This event is closed.')) {
                        appendError(errors, 'event', {
                            message: 'This event is closed.',
                            type: PURCHASE_EVENT_CLOSED
                        });
                    }
                    if (data.message.event.includes('Maximum entries number for this event has been reached.')) {
                        appendError(errors, 'event', {
                            message: 'This event is full.',
                            type: PURCHASE_EVENT_FULL
                        });
                    }
                    const insufficientApiRegex = /Sorry, you do not have enough money in your account, please select another (pool|contest|event) or deposit more money into your account\./i;
                    if (data.message.event.some((message) => (insufficientApiRegex.test(message)))) {
                        appendError(errors, 'event', {
                            message: 'You do not have enough tokens to enter this event.',
                            type: PURCHASE_INSUFFICIENT_FUNDS
                        });
                    }
                }
                const ethWalletRequiredMessage = 'Customer has no Ethereum wallet associated with their account.';
                if (((_d = (_c = data === null || data === void 0 ? void 0 : data.message) === null || _c === void 0 ? void 0 : _c.user) !== null && _d !== void 0 ? _d : []).includes(ethWalletRequiredMessage)) {
                    appendError(errors, 'user', {
                        message: 'This event requires that you have an Ethereum Wallet connected and have the ' +
                            'required NFT(s) for this event.',
                        type: PURCHASE_CONNECTED_WALLET_REQUIRED
                    });
                }
                const nftRequiredMessage = 'Customer doesn\'t have NFT required to enter event.';
                if (((_f = (_e = data === null || data === void 0 ? void 0 : data.message) === null || _e === void 0 ? void 0 : _e.user) !== null && _f !== void 0 ? _f : []).includes(nftRequiredMessage)) {
                    appendError(errors, 'user', {
                        message: 'You do not have the required NFT(s) to enter this event',
                        type: PURCHASE_NFT_REQUIRED
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    put(url, data) {
        return this.fetch(url, {
            body: JSON.stringify(data),
            method: 'PUT',
        });
    }
    async register(providedData) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        const registerUrl = await this.getQueryUrlWithGeolocation('/v1/register/');
        try {
            const data = {
                confirmPassword: providedData.password,
                email: providedData.email,
                password: providedData.password,
                province: providedData.province,
                username: providedData.username,
            };
            await this.post(registerUrl, data);
            await this.attemptAuthorization();
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (((_a = data.register) === null || _a === void 0 ? void 0 : _a.username) === 'Username not available.') {
                    appendError(errors, 'username', {
                        message: (_b = data.register) === null || _b === void 0 ? void 0 : _b.username,
                        type: USERNAME_NOT_AVAILABLE,
                    });
                }
                else if ((_c = data.register) === null || _c === void 0 ? void 0 : _c.username) {
                    appendError(errors, 'username', {
                        message: (_d = data.register) === null || _d === void 0 ? void 0 : _d.username,
                        type: USERNAME_INVALID,
                    });
                }
                if (((_e = data.register) === null || _e === void 0 ? void 0 : _e.email) === 'Email already exists.') {
                    appendError(errors, 'email', {
                        message: (_f = data.register) === null || _f === void 0 ? void 0 : _f.email,
                        type: REGISTER_EMAIL_EXISTS,
                    });
                }
                else if ((_g = data.register) === null || _g === void 0 ? void 0 : _g.email) {
                    appendError(errors, 'email', {
                        message: (_h = data.register) === null || _h === void 0 ? void 0 : _h.email,
                        type: EMAIL_INVALID,
                    });
                }
                if ((_j = data.register) === null || _j === void 0 ? void 0 : _j.stateprov) {
                    appendError(errors, 'province', {
                        message: data.register.stateprov,
                        type: STATE_PROVINCE_INVALID,
                    });
                }
                if ((_k = data.register) === null || _k === void 0 ? void 0 : _k.password) {
                    for (const passwordError of Object.values((_l = data.register) === null || _l === void 0 ? void 0 : _l.password)) {
                        appendError(errors, 'password', {
                            message: passwordError,
                            type: PASSWORD_INVALID,
                        });
                    }
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    async cryptoRegister(providedData) {
        var _a, _b;
        const registerUrl = await this.getQueryUrlWithGeolocation('/v1/crypto_register/');
        try {
            const data = {
                address: providedData.address,
                signature: providedData.signature,
            };
            await this.post(registerUrl, data);
            await this.attemptCryptoAuthorization();
        }
        catch (error) {
            const { data, type } = error;
            if (type === HTTP_ERROR_BAD_REQUEST) {
                const errors = { type };
                if (((_a = data.register) === null || _a === void 0 ? void 0 : _a.address) === 'Address not available.') {
                    appendError(errors, 'address', {
                        message: (_b = data.register) === null || _b === void 0 ? void 0 : _b.address,
                        type: USERNAME_NOT_AVAILABLE,
                    });
                }
                return Promise.reject(errors);
            }
            return Promise.reject(error);
        }
    }
    validateUsername(username) {
        const registerUrl = this.getQueryUrl('/v1/register/username/' + username);
        return this.get(registerUrl).then((response) => {
            return response.username;
        }).catch((e) => {
            return null;
        });
    }
    autoAuth(immediate) {
        const customerRefreshTimeout = immediate ? 0 : this.options.customerRefreshInterval;
        this.autoAuthTimeoutId = window.setTimeout((() => {
            this.fetchCurrentCustomer()
                .then((customer) => {
                if (!customer) {
                    return;
                }
                for (const callback of this.onAutoAuthCallbacks) {
                    callback(customer);
                }
            })
                .then(this.autoAuth.bind(this, false), this.autoAuth.bind(this, false));
        }), customerRefreshTimeout);
    }
    endWithSlash(url) {
        let outputUrl = url;
        if (url && url[url.length - 1] !== '/') {
            outputUrl += '/';
        }
        return outputUrl;
    }
    async fetch(url, extraOptions) {
        var _a, _b;
        let isMultipart = false;
        if (url.includes('/v1/avatars/')) {
            isMultipart = true;
        }
        const fetchOptions = Object.assign(Object.assign({}, this.getBaseFetchOptions(isMultipart)), extraOptions);
        let tokenAuthenticationQueryString = '';
        if (this.options.authenticationMethod === 'token' && this.authorization) {
            tokenAuthenticationQueryString =
                `${url.includes('?') ? '&' : '?'}username=${(_a = this.authorization) === null || _a === void 0 ? void 0 : _a.username}` +
                    `&api_key=${(_b = this.authorization) === null || _b === void 0 ? void 0 : _b.apiKey}`;
        }
        const fullUrl = this.stripDoubleSlashes(`${this.options.baseUrl}${url}${tokenAuthenticationQueryString}`);
        const response = await fetch(fullUrl, fetchOptions);
        const contentType = response.headers.get('content-type');
        const contentLength = parseInt(response.headers.get('content-length') || '0', BASE_10);
        let responseData;
        if (contentType === 'application/json') {
            responseData = await response.json();
        }
        else if (contentLength > 0) {
            responseData = await response.text();
        }
        const { status } = response;
        if (status >= HTTP_STATUS_OK_START && status <= HTTP_STATUS_OK_END) {
            return Promise.resolve(responseData);
        }
        else if (status === HTTP_STATUS_BAD_REQUEST) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_BAD_REQUEST,
            });
        }
        else if (status === HTTP_STATUS_NOT_AUTHORIZED) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_NOT_AUTHORIZED,
            });
        }
        else if (status === HTTP_STATUS_FORBIDDEN) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_FORBIDDEN,
            });
        }
        else if (status === HTTP_STATUS_NOT_FOUND) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_NOT_FOUND,
            });
        }
        else if (status >= HTTP_STATUS_SERVER_ERROR_START && status <= HTTP_STATUS_SERVER_ERROR_END) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_SERVER_ERROR,
            });
        }
        else if (status >= HTTP_STATUS_SERVICE_UNAVAILABLE_START && status <= HTTP_STATUS_SERVICE_UNAVAILABLE_END) {
            return Promise.reject({
                data: responseData,
                type: HTTP_ERROR_SERVICE_UNAVAILABLE,
            });
        }
        return Promise.reject({
            data: responseData,
            type: UNKNOWN_ERROR,
        });
    }
    getBaseFetchOptions(isMultipart = false) {
        const { basicAuth } = this.options;
        let contentType = 'application/json';
        if (isMultipart) {
            contentType = 'multipart/form-data';
        }
        const headers = new Headers({
            'content-type': contentType,
        });
        if (basicAuth) {
            const basicAuthCode = btoa(`${basicAuth.username}:${basicAuth.password}`);
            headers.append('Authorization', `Basic ${basicAuthCode}`);
        }
        if (this.authorization) {
            headers.append('X-CSRFToken', this.authorization.csrfToken);
        }
        if (typeof this.options.platform !== 'undefined') {
            headers.append('Client-Id', this.options.platform);
        }
        headers.append('Referer', this.options.baseUrl);
        const options = {
            cache: 'no-cache',
            credentials: 'include',
            headers,
            method: 'GET',
            mode: 'cors',
            redirect: 'follow',
        };
        return options;
    }
    getQueryString(params) {
        if (!params) {
            return '';
        }
        const parts = [];
        for (const key in params) {
            if (!params[key]) {
                continue;
            }
            parts.push(`${key}=${encodeURIComponent(String(params[key]))}`);
        }
        return parts.length ? `?${parts.join('&')}` : '';
    }
    getQueryUrl(providedUrl, params, methodSpecificTranslateKey) {
        if (!params) {
            return providedUrl;
        }
        const keys = Object.keys(params);
        if (keys.length === 0) {
            return providedUrl;
        }
        const parts = keys.map((key) => {
            const param = params[key];
            let value = param;
            if (param instanceof Array && param.length > 0) {
                const values = param.reduce((previousValues, currentValue) => {
                    const newValues = [...previousValues];
                    newValues.push(currentValue);
                    return newValues;
                }, []);
                value = values.join(',');
            }
            return `${encodeURIComponent(this.translateQueryStringKey(key, methodSpecificTranslateKey))}=${encodeURIComponent(String(value))}`;
        });
        return `${providedUrl}?${parts.join('&')}`;
    }
    startAutoAuth() {
        this.stopAutoAuth();
        this.autoAuth(true);
    }
    stopAutoAuth() {
        if (this.autoAuthTimeoutId) {
            clearInterval(this.autoAuthTimeoutId);
        }
    }
    stripDoubleSlashes(url) {
        return url.replace(new RegExp('//', 'g'), '/').replace(':/', '://');
    }
    translatePurchaseTicketError(rawErrorMessage) {
        if (typeof rawErrorMessage === 'object') {
            const errorKeys = Object.keys(rawErrorMessage);
            if (errorKeys.find((key) => (key === 'unavailable'))) {
                return { type: PURCHASE_INVALID_OUTCOME };
            }
            if (errorKeys.find((key) => (key === 'insuf'))) {
                return { type: PURCHASE_INSUFFICIENT_FUNDS };
            }
        }
        else if (typeof rawErrorMessage === 'string') {
            if (rawErrorMessage === 'Maximum entries number for this event has been reached.') {
                return { type: PURCHASE_EVENT_FULL };
            }
            if (rawErrorMessage.includes('is closed.')) {
                return { type: PURCHASE_EVENT_CLOSED };
            }
            if (rawErrorMessage === 'The event does not exist') {
                return { type: PURCHASE_EVENT_DOES_NOT_EXIST };
            }
            if (rawErrorMessage === 'Sorry, you do not have enough money in your account,' +
                'please select another pool or deposit more money into your account.') {
                return { type: PURCHASE_INSUFFICIENT_FUNDS };
            }
            if (rawErrorMessage.includes('cannot be drafted')
                || rawErrorMessage.includes('has already been locked')
                || rawErrorMessage === 'The outcome does not exist') {
                return { type: PURCHASE_INVALID_OUTCOME };
            }
        }
        return {
            data: {
                message: rawErrorMessage,
            },
            type: UNKNOWN_ERROR,
        };
    }
    translateQueryStringKey(key, methodSpecificTranslateKey) {
        if (typeof key === 'number') {
            return key.toString();
        }
        const queryStringKeywords = ['In', 'Eq', 'Gt', 'Lt', 'Gte', 'Lte'];
        let translatedKey = key;
        for (const keywordIndex in queryStringKeywords) {
            if (!key.endsWith(queryStringKeywords[keywordIndex])) {
                continue;
            }
            translatedKey = key.replace(queryStringKeywords[keywordIndex], `__${queryStringKeywords[keywordIndex].toLowerCase()}`);
            break;
        }
        if (methodSpecificTranslateKey !== undefined) {
            translatedKey = methodSpecificTranslateKey(translatedKey);
        }
        return this.camelToSnakeString(translatedKey);
    }
    translateRawProduct(raw) {
        const { settings_json } = raw;
        let thumbnail;
        let displayItemsOrder;
        if (settings_json) {
            const { client_settings } = settings_json;
            if (client_settings) {
                thumbnail = client_settings.thumbnail;
                displayItemsOrder = client_settings.display_items_order;
            }
        }
        return {
            abbrev: raw.abbrev,
            id: String(raw.id),
            name: raw.name,
            settings: {
                clientSettings: {
                    displayItemsOrder,
                    thumbnail,
                },
            },
        };
    }
    translateRawProductPlayType(ppt) {
        const { settings_json } = ppt;
        const { client_settings } = settings_json;
        let thumbnail;
        if (client_settings) {
            thumbnail = client_settings.thumbnail;
        }
        const rawEmbed = client_settings ? client_settings.embed : undefined;
        const logo = rawEmbed ? rawEmbed.logo : undefined;
        const embed = rawEmbed ? {
            height: rawEmbed.height,
            logo: {
                alt: (logo && logo.alt) ? logo.alt : undefined,
                className: (logo && logo.class_name) ? logo.class_name : undefined,
                url: (logo && logo.url) ? logo.url : undefined,
            },
            primaryColor: rawEmbed.primary_color,
            url: rawEmbed.url,
            width: rawEmbed.width,
        } : undefined;
        const productPlayType = {
            abbrev: ppt.abbrev,
            id: String(ppt.id),
            name: ppt.name,
            productId: String(ppt.product_id),
            settings: {
                bonusMultipliers: settings_json.bonus_multipliers,
                clientSettings: {
                    embed,
                    thumbnail,
                },
                maxAdvanceBuy: settings_json.max_advance_buy,
                prizeTable: settings_json.prize_table,
                ticketCosts: settings_json.ticket_costs,
                ticketPrizeMultiplier: settings_json.ticket_prize_multiplier,
            },
        };
        return productPlayType;
    }
    translateRawSelections(raws) {
        const selections = [];
        for (const raw of raws) {
            const selectionBase = {
                id: raw.id,
                outcomeId: raw.outcome ? raw.outcome.id : undefined,
                losingOutcomeId: raw.losing_outcome_id ? raw.losing_outcome_id : undefined,
                pointsEarned: raw.points_earned,
            };
            let selection;
            if (raw.bet_id !== undefined) {
                selection = Object.assign(Object.assign({}, selectionBase), { bedId: raw.bet_id, value: raw.value });
            }
            else {
                selection = Object.assign(Object.assign({}, selectionBase), { eventPositionId: raw.event_position_id });
            }
            if (raw.outcome) {
                const outcome = {
                    externalId: raw.outcome.external_id,
                    id: raw.outcome.id,
                    imageUrl: raw.outcome.image_url,
                    name: raw.outcome.name,
                    plannedPoints: raw.outcome.planned_points,
                    pointsAvailable: raw.outcome.points_available,
                    relatedCollection: raw.outcome.related_collection ? String(raw.outcome.related_collection) : undefined,
                    selectionCost: raw.outcome.selection_cost,
                    type: raw.outcome.type ? {
                        id: raw.outcome.type.id,
                        name: raw.outcome.type.name,
                    } : undefined,
                };
            }
            selections.push(selection);
        }
        return selections;
    }
    camelToSnakeObject(inputObject, deep = false) {
        if (!inputObject) {
            return inputObject;
        }
        const output = {};
        for (const key in inputObject) {
            if (!key) {
                continue;
            }
            const newKey = this.camelToSnakeString(key);
            const val = inputObject[key];
            output[newKey] = (deep && typeof val === 'object') ?
                this.camelToSnakeObject(val) :
                val;
        }
        return output;
    }
    camelToSnakeString(inString) {
        const uppercases = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        return inString.split('').map((char) => {
            return (uppercases.includes(char) ? `_${char.toLowerCase()}` : char);
        }).join('');
    }
    fetchList(baseUrl, options, transform) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl(baseUrl, options);
            this.fetch(url)
                .then((response) => {
                const { objects } = response;
                const components = objects.map((raw) => {
                    return transform(raw);
                });
                yes(components);
            }, no);
        });
    }
    translateRawParrotPassTokenClaimStatus(rawPPClaimStatus) {
        const parrotPassClaimStatus = {
            passId: rawPPClaimStatus.pass_id,
            claimedTokens: rawPPClaimStatus.claimed_tokens
        };
        if (typeof rawPPClaimStatus.claim_attempt_status !== 'undefined') {
            parrotPassClaimStatus.claimAttemptStatus = rawPPClaimStatus.claim_attempt_status;
        }
        return parrotPassClaimStatus;
    }
    translateRawEventGames(rawEventGames) {
        const { config, settings_json: settingsJson = '{}' } = rawEventGames;
        const settings = JSON.parse(settingsJson);
        const { settings_json: rawConfigSettingsJson = '{}' } = config;
        const rawConfigSettings = JSON.parse(rawConfigSettingsJson);
        return {
            closeEventTs: rawEventGames.close_event,
            config: {
                gameType: config.game_type,
                gameVersion: rawConfigSettings.version,
                hidden: config.hidden,
                id: config.id,
                name: config.name,
            },
            finalizeEventTs: rawEventGames.finalize_event,
            games: rawEventGames.games.map((rawGame) => (rawGame.id)),
            id: rawEventGames.id,
            league: rawEventGames.context,
            name: rawEventGames.name || '',
            openEventTs: rawEventGames.open_event,
            settings,
        };
    }
    translateRawEventGamesGames(rawEventGames) {
        return rawEventGames.games.map((rawGame) => {
            if (rawGame.league === 'PGA') {
                const parsedSettings = JSON.parse(rawGame.settings_json);
                return {
                    course: parsedSettings.clubs,
                    externalId: rawGame.external_id,
                    finalized: rawGame.finalized,
                    gameCodeGlobalId: rawGame.game_code_global_id,
                    gameStatus: rawGame.game_status,
                    gameTime: rawGame.game_time,
                    id: rawGame.id,
                    league: rawGame.league,
                    provider: rawGame.provider,
                    season: rawGame.season,
                    timeRemaining: rawGame.time_remaining,
                    tournamentDisplayName: parsedSettings.tournament_display_name,
                    tournamentName: parsedSettings.tournament_name,
                };
            }
            else if (rawGame.league === 'MMA') {
                const parsedSettings = JSON.parse(rawGame.settings_json);
                return {
                    externalId: rawGame.external_id,
                    finalized: rawGame.finalized,
                    gameCodeGlobalId: rawGame.game_code_global_id,
                    gameStatus: rawGame.game_status,
                    gameTime: rawGame.game_time,
                    id: rawGame.id,
                    league: rawGame.league,
                    provider: rawGame.provider,
                    season: rawGame.season,
                    timeRemaining: rawGame.time_remaining,
                    tournamentDisplayName: parsedSettings.name,
                    tournamentName: parsedSettings.short_name,
                };
            }
            else if (rawGame.league === 'NASCAR') {
                const parsedSettings = JSON.parse(rawGame.settings_json);
                return {
                    course: parsedSettings.track,
                    externalId: rawGame.external_id,
                    finalized: rawGame.finalized,
                    gameCodeGlobalId: rawGame.game_code_global_id,
                    gameStatus: rawGame.game_status,
                    gameTime: rawGame.game_time,
                    id: rawGame.id,
                    league: rawGame.league,
                    provider: rawGame.provider,
                    season: rawGame.season,
                    timeRemaining: rawGame.time_remaining,
                    tournamentDisplayName: parsedSettings.name,
                    tournamentName: parsedSettings.name,
                };
            }
            return {
                externalId: rawGame.external_id,
                finalized: rawGame.finalized,
                gameCodeGlobalId: rawGame.game_code_global_id,
                gameStatus: rawGame.game_status,
                gameTime: rawGame.game_time,
                gameUnit: rawGame.game_unit,
                homeTeamId: rawGame.home_team_id,
                homeTeamName: rawGame.home_team.name,
                homeTeamScore: rawGame.home_team_score,
                id: rawGame.id,
                league: rawGame.league,
                provider: rawGame.provider,
                season: rawGame.season,
                timeRemaining: rawGame.time_remaining,
                visitingTeamId: rawGame.visiting_team_id,
                visitingTeamName: rawGame.visiting_team.name,
                visitingTeamScore: rawGame.visiting_team_score,
                week: rawGame.week,
            };
        });
    }
    translateRawEventGamesTeams(rawEventGames) {
        const inferTeamVenueType = (domeType) => {
            switch (domeType) {
                case TEAM_VENUE_DOME:
                    return 'dome';
                case TEAM_VENUE_OUTDOORS:
                    return 'outdoor';
                case TEAM_VENUE_RETRACTABLE:
                    return 'retractable-roof';
                default:
                    return 'indoor';
            }
        };
        const teams = rawEventGames.games.reduce((gamesReducerTeams, rawGame) => {
            const parsedTeams = [rawGame.home_team, rawGame.visiting_team]
                .reduce((reducerTeams, rawTeam) => {
                if (rawTeam) {
                    const parsedTeamSettings = rawTeam.settings_json.length > 0
                        ? JSON.parse(rawTeam.settings_json)
                        : undefined;
                    const teamVenue = parsedTeamSettings ? {
                        bearing: parsedTeamSettings.bearing,
                        city: parsedTeamSettings.stadium_city,
                        coordinates: {
                            lat: parsedTeamSettings.latitude,
                            long: parsedTeamSettings.longitude,
                        },
                        country: parsedTeamSettings.stadium_country,
                        name: parsedTeamSettings.stadium_name,
                        state: parsedTeamSettings.stadium_state,
                        type: inferTeamVenueType(parsedTeamSettings.dome),
                    } : undefined;
                    const team = {
                        alias: rawTeam.alias,
                        city: rawTeam.city,
                        conference: rawTeam.conference,
                        division: rawTeam.division,
                        externalId: rawTeam.external_id,
                        id: rawTeam.id,
                        league: rawTeam.league,
                        losses: rawTeam.losses,
                        name: rawTeam.name,
                        provider: rawTeam.provider,
                        season: rawTeam.season,
                        teamCodeGlobalId: rawTeam.team_code_global_id,
                        venue: teamVenue,
                        wins: rawTeam.wins,
                    };
                    return [...reducerTeams, team];
                }
                return reducerTeams;
            }, []);
            return [...gamesReducerTeams, ...parsedTeams];
        }, []);
        return teams.reduce((uniqueReducerTeams, team) => {
            if (uniqueReducerTeams.find((uniqueTeam) => uniqueTeam.id === team.id)) {
                return uniqueReducerTeams;
            }
            return [...uniqueReducerTeams, team];
        }, []);
    }
    fetchLobbyContent(options) {
        return new Promise((yes, no) => {
            const url = this.getQueryUrl('/v1/lobbycontent/', options);
            this.get(url)
                .then((rawLobbyContents) => {
                const lobbyContent = rawLobbyContents.objects.map((rawLobbyContent) => {
                    const content = {
                        contentType: rawLobbyContent.content_type,
                        createdTs: rawLobbyContent.created_ts,
                        description: rawLobbyContent.description,
                        endTs: rawLobbyContent.end_ts,
                        event: rawLobbyContent.event ? this.translateEvent(rawLobbyContent.event) : undefined,
                        externalUrl: rawLobbyContent.external_url,
                        id: rawLobbyContent.id,
                        imageUrl: rawLobbyContent.image_url,
                        league: rawLobbyContent.league,
                        market: rawLobbyContent.market ? {
                            active: rawLobbyContent.market.active,
                            createdTimestamp: rawLobbyContent.market.created_ts,
                            icon: rawLobbyContent.market.icon,
                            id: rawLobbyContent.market.id,
                            modifiedTimestamp: rawLobbyContent.market.modified_ts,
                            name: rawLobbyContent.market.name,
                            order: rawLobbyContent.market.order,
                            settings: JSON.parse(rawLobbyContent.market.settings_json),
                        } : undefined,
                        marketEvent: rawLobbyContent.market_event ? {
                            active: rawLobbyContent.market_event.active,
                            createdTimestamp: rawLobbyContent.market_event.created_ts,
                            eventIds: rawLobbyContent.market_event.events.map((event) => event.id),
                            eventTimestamp: rawLobbyContent.market_event.event_ts,
                            icon: rawLobbyContent.market_event.icon,
                            id: rawLobbyContent.market_event.id,
                            marketId: rawLobbyContent.market_event.market_id,
                            modifiedTimestamp: rawLobbyContent.market_event.modified_ts,
                            name: rawLobbyContent.market_event.name,
                            settings: JSON.parse(rawLobbyContent.market_event.settings_json),
                        } : undefined,
                        settingsJson: rawLobbyContent.settings_json,
                        sortOrder: rawLobbyContent.sort_order,
                        startTs: rawLobbyContent.start_ts,
                        title: rawLobbyContent.title,
                    };
                    return content;
                });
                yes(lobbyContent);
            }, no);
        });
    }
}
